import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import baseUrl from '../BaseUrl';
import FirebaseFeedbackFilters from './FirebaseFeedbackFilters';
import FirebaseFeedbackSummaryPanel from './FirebaseFeedbackSummaryPanel';
import FirebaseFeedbackTable from './FirebaseFeedbackTable';
import FirebaseMessageModal from './FirebaseMessageModal';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import '../active-users/active-users.css';
import './firebase-feedback.css';

const PAGE_SIZE = 20;
const FILTER_KEY = 'firebaseFeedbacksState';
const EMPTY_FILTERS = { search: '', from: '', to: '', rating: '' };

/**
 * Firebase Feedback — the TAB BODY (feature 067-user-feedback-tabs).
 *
 * Replaces the old card list at /firebase-feedback, which had no filters, no export, a Load-More
 * button and an unauthenticated API. This is the same shape as its three sibling tabs.
 *
 * ── THE REASON THIS WAS REBUILT RATHER THAN RE-SKINNED ───────────────────────────────────────
 *
 * The screen it replaces showed **102 of the collection's 154 documents** and reported 102 as the
 * total. Its Firestore query filtered and ordered on `createdAt`, and both are EXISTENCE filters:
 * documents written by app generations that spelled the field differently — or omitted it — were
 * excluded from the result entirely. 52 messages members took the trouble to write had never
 * appeared on screen, and nothing in the UI hinted at it.
 *
 * The reconciliation lives in services/firebaseFeedbackService.js. What matters here is that the
 * total this tab prints is now the whole collection.
 */
export default function FirebaseFeedbackBody() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(() => readSavedFilters(FILTER_KEY) || EMPTY_FILTERS);
  const [summary, setSummary] = useState(null);
  const [notice, setNotice] = useState('');
  // The one message being read in full, or null. The row itself is held, not just its text, so
  // the popup can name who wrote it and when.
  const [openMessage, setOpenMessage] = useState(null);

  usePersistFilters(FILTER_KEY, filters);

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== '' && v !== null && v !== undefined
  );

  const rangeStart = totalItem === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItem);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      if (expire * 1000 < new Date().getTime()) {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); }
    }
  };

  useEffect(() => {
    if (token) { loadList(page, filters); loadSummary(filters); }
    /* eslint-disable-next-line */
  }, [token]);

  const loadList = async (toPage, f) => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/firebase-feedbacks`, {
        params: { ...f, page: toPage, limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (res.data && res.data.data) || {};
      setItems(data.feedbacks || []);
      setTotalItem((data.pagination && data.pagination.total) || 0);
      setPage(toPage);
    } catch (e) {
      // Never an empty list: "we could not read the collection" and "no member has written" must
      // not look the same — which is the failure mode this whole tab exists to correct.
      setError('Failed to load feedback.');
      setItems([]);
      setTotalItem(0);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async (f) => {
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/firebase-feedbacks/summary`, {
        params: f,
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary((res.data && res.data.data) || null);
    } catch (e) {
      setSummary(null); // supporting detail; its failure must not blank the list
    }
  };

  const handleApply = (f) => { setFilters(f); loadList(1, f); loadSummary(f); };
  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    loadList(1, EMPTY_FILTERS);
    loadSummary(EMPTY_FILTERS);
  };

  const handleExport = async () => {
    setNotice('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/firebase-feedbacks/export`, {
        params: filters,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.headers && res.headers['x-export-truncated'] === 'true') {
        setNotice('This export hit the 50,000 row cap. Narrow the filters to get the rest.');
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'firebase-feedbacks.csv');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setNotice('Export failed. Please try again.');
    }
  };

  return (
    <>
      <h2 className="au-title uf-tab-title ff-title">In-App Feedback Section</h2>
      <p className="ff-subtitle">
        {loading ? 'Loading feedback…' : `${totalItem} message${totalItem === 1 ? '' : 's'}`}
        {!loading && totalItem > 0 && (
          <span className="ff-range"> · showing {rangeStart}–{rangeEnd}</span>
        )}
      </p>

      <FirebaseFeedbackFilters
        values={filters}
        onApply={handleApply}
        onClear={handleClear}
        onExport={handleExport}
      />

      {notice && <div className="ff-notice" role="alert">{notice}</div>}

      <FirebaseFeedbackSummaryPanel summary={summary} />

      {loading ? (
        <TableSkeleton rows={8} />
      ) : error ? (
        <ErrorState message={error} onRetry={() => loadList(page, filters)} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No feedback yet"
          message="No member has sent in-app feedback for the current filters."
          hasFilters={hasActiveFilters}
          onClear={handleClear}
        />
      ) : (
        <>
          <FirebaseFeedbackTable items={items} onOpenMessage={setOpenMessage} />
          {totalItem > PAGE_SIZE && (
            <div className="ff-pagination">
              <Pagination
                activePage={page}
                itemsCountPerPage={PAGE_SIZE}
                totalItemsCount={totalItem}
                pageRangeDisplayed={5}
                onChange={(p) => loadList(p, filters)}
                itemClass="page-item"
                linkClass="page-link"
              />
            </div>
          )}
        </>
      )}

      <FirebaseMessageModal item={openMessage} onClose={() => setOpenMessage(null)} />
    </>
  );
}
